'use client'

import { useAtomValue, useStore } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { type ReactNode, useMemo, useRef, useState } from 'react'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ScrollableTabRow } from '@/components/widget/scrollable-tab-row'
import { getEffectivePricePrecision } from '@/features/dashboard/lib/orderbook-data'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { resolveFeeTier } from '@/features/order/order-form/lib/resolve-fee-tier'
import { useNow } from '@/hooks/use-now'
import { useNumberFormat } from '@/hooks/use-number-format'
import { formatFundingCountdown, resolveFundingSchedule } from '@/lib/next-funding'
import { cn } from '@/lib/utils'
import { useMarketDataStream } from '@/service/ws/use-market-data-stream'
import { accountFeesByIdAtom } from '@/state/account'
import {
  scheduleTickerMapWrite,
  type WatchListTickerMap,
  watchListTickerMapAtom,
} from '@/state/dashboard'
import {
  Exchange,
  type Instrument,
  MarketDataType,
  type Ticker,
} from '@/types/generated/common/types'

type ChartMarketTicker = WatchListTickerMap[string] | null
type ChartMarketTickerValue = NonNullable<ChartMarketTicker>

type MarketStatItem = {
  label: string
  value: string
  tooltip: string
  emphasis?: boolean
  className?: string
  /** Colours the value alone, leaving the suffix in the row's default colour. */
  valueClassName?: string
  /** Trailing detail after the value, in the value's own size (e.g. " / " + the next-funding
   * countdown); it carries its own separator. */
  suffix?: ReactNode
}

const STICKY_MARKET_TICKER_FIELDS = [
  'lastPrice',
  'markPrice',
  'indexPrice',
  'volume24h',
  'priceChange24h',
  'priceChangePercent24h',
  'high24h',
  'low24h',
  'openInterest',
  'fundingRate',
  'nextFundingTime',
] as const satisfies readonly (keyof ChartMarketTickerValue)[]

const POSITIVE_MARKET_TICKER_FIELDS = new Set<keyof ChartMarketTickerValue>([
  'nextFundingTime',
  'lastPrice',
  'markPrice',
  'indexPrice',
  'volume24h',
  'high24h',
  'low24h',
  'openInterest',
])

function isValidMarketTickerValue(field: keyof ChartMarketTickerValue, value: string | undefined) {
  if (value === undefined || value === '') return false
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return false
  if (POSITIVE_MARKET_TICKER_FIELDS.has(field)) return numberValue > 0
  return true
}

function mergeStickyMarketTicker(
  previous: ChartMarketTicker,
  current: ChartMarketTicker
): ChartMarketTicker {
  if (!current) return previous
  const next: ChartMarketTickerValue = { ...(previous ?? {}) }

  for (const field of STICKY_MARKET_TICKER_FIELDS) {
    const value = current[field]
    if (isValidMarketTickerValue(field, value)) {
      next[field] = value
    }
  }

  return next
}

function useStickyMarketTicker(symbol: string | null, ticker: ChartMarketTicker) {
  const stickyTickerRef = useRef<{ symbol: string | null; ticker: ChartMarketTicker }>({
    symbol: null,
    ticker: null,
  })

  if (stickyTickerRef.current.symbol !== symbol) {
    stickyTickerRef.current = {
      symbol,
      ticker: mergeStickyMarketTicker(null, ticker),
    }
  } else {
    stickyTickerRef.current = {
      symbol,
      ticker: mergeStickyMarketTicker(stickyTickerRef.current.ticker, ticker),
    }
  }

  return stickyTickerRef.current.ticker
}

function tickerToMarketTicker(ticker: Ticker | null): ChartMarketTicker {
  if (!ticker) return null

  return {
    lastPrice: ticker.lastPrice,
    markPrice: ticker.markPrice,
    indexPrice: ticker.indexPrice,
    bestBidPrice: ticker.bidPrice,
    bestAskPrice: ticker.askPrice,
    volume24h: ticker.volume24h,
    priceChange24h: ticker.priceChange24h,
    priceChangePercent24h: ticker.priceChangePercent24h,
    high24h: ticker.high24h,
    low24h: ticker.low24h,
    openInterest: ticker.openInterest,
    fundingRate: ticker.fundingRate,
    nextFundingTime: ticker.nextFundingTime,
  }
}

function writeTickerToCache(store: ReturnType<typeof useStore>, ticker: Ticker) {
  const incoming = tickerToMarketTicker(ticker)
  if (!incoming) return

  const key = watchlistEntryKey(ticker.exchange, ticker.symbol)
  scheduleTickerMapWrite(store, key, (current) => {
    const merged = mergeStickyMarketTicker(current ?? null, incoming)
    if (!merged) return current

    const nextEntry: WatchListTickerMap[string] = {
      ...current,
      ...merged,
      prevLastPrice: current?.lastPrice ?? merged.lastPrice,
      prevBestBidPrice: current?.bestBidPrice ?? merged.bestBidPrice,
      prevBestAskPrice: current?.bestAskPrice ?? merged.bestAskPrice,
    }

    const isChanged = Object.keys(nextEntry).some((key) => {
      const field = key as keyof typeof nextEntry
      return current?.[field] !== nextEntry[field]
    })

    return isChanged ? nextEntry : current
  })
}

function formatNumber(
  value: string | number | undefined,
  maximumFractionDigits = 2,
  minimumFractionDigits = 0
) {
  if (value === undefined || value === null || value === '') return '-'
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return '-'
  return numberValue.toLocaleString('en-US', {
    maximumFractionDigits,
    minimumFractionDigits,
  })
}

function formatPrice(
  value: string | number | undefined,
  pricePrecision?: string,
  instrument?: Instrument | null
) {
  if (value === undefined || value === null || value === '') return '-'
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return '-'
  const decimals =
    getEffectivePricePrecision(instrument, numberValue) ??
    getEffectivePricePrecision({ tickSize: pricePrecision }, numberValue) ??
    2
  return numberValue.toLocaleString('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  })
}

function formatPercent(value: string | number | undefined, maximumFractionDigits = 2) {
  if (value === undefined || value === null || value === '') return '-'
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return '-'
  const signedValue =
    numberValue > 0
      ? `+${formatNumber(numberValue, maximumFractionDigits, maximumFractionDigits)}`
      : formatNumber(numberValue, maximumFractionDigits, maximumFractionDigits)
  return `${signedValue}%`
}

function formatFunding(value: string | number | undefined) {
  if (value === undefined || value === null || value === '') return '-'
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return '-'
  const percentValue = Math.abs(numberValue) < 1 ? numberValue * 100 : numberValue
  return formatPercent(percentValue, 4)
}

function formatFeeRate(value: string | number | undefined) {
  if (value === undefined || value === null || value === '') return '-'
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return '-'
  return formatPercent(numberValue * 100, 4)
}

/** Ticks on its own so only this span re-renders each second, not the whole stats row. */
function FundingCountdown({ at }: { at: Date }) {
  const now = useNow(1000)
  return <>{formatFundingCountdown(at.getTime() - now)}</>
}

export function ChartMarketStatsRow({
  symbol,
  exchange,
  pricePrecision,
  instrument,
  accountId,
}: {
  symbol: string
  exchange: Exchange
  pricePrecision?: string
  instrument?: Instrument | null
  accountId?: string
}) {
  const t = useTranslations('trade.chartToolbar.marketStats')
  const fmt = useNumberFormat()
  const store = useStore()
  const [streamTicker, setStreamTicker] = useState<Ticker | null>(null)
  const accountFeesById = useAtomValue(accountFeesByIdAtom)
  const accountFees = (accountId && accountFeesById[accountId]) || null
  const feeTier = instrument ? resolveFeeTier(instrument, accountFees) : null
  const makerFee = feeTier?.makerFee ?? instrument?.makerCommission
  const takerFee = feeTier?.takerFee ?? instrument?.takerCommission
  const cachedTicker = useAtomValue(
    useMemo(
      () =>
        selectAtom(
          watchListTickerMapAtom,
          (map) => map[watchlistEntryKey(exchange, symbol)] ?? null
        ),
      [exchange, symbol]
    )
  )

  // Reset synchronously during render (not in a useEffect) when the symbol/exchange
  // changes — an effect only fires AFTER the first paint with the new symbol, so for
  // that one render `streamTicker` would still hold the PREVIOUS tab's live ticker.
  // mergedTicker below has no way to tell that data apart from genuinely fresh data for
  // the new symbol, so it gets blended in via mergeStickyMarketTicker's "keep last valid
  // value" logic — showing a mix of old-symbol fields until the next WS message arrives.
  // Worse, useStickyMarketTicker latches that wrong blend as its new sticky baseline, so
  // stale/wrong fields (whichever ones happened to be valid on the OLD symbol at that
  // instant) can persist on screen indefinitely instead of just for one frame.
  const streamResetKeyRef = useRef<string | null>(null)
  const streamResetKey = `${exchange}:${symbol}`
  if (streamResetKeyRef.current !== streamResetKey) {
    streamResetKeyRef.current = streamResetKey
    if (streamTicker !== null) setStreamTicker(null)
  }

  useMarketDataStream({
    connected: Boolean(symbol),
    payload: {
      exchange,
      symbol,
      dataType: MarketDataType.MARKET_DATA_TYPE_TICKER,
    },
    onMessage: (data) => {
      const nextTicker = data?.tickers?.find(
        (ticker) => ticker.symbol === symbol && ticker.exchange === exchange
      )
      if (!nextTicker) return
      setStreamTicker(nextTicker)
      writeTickerToCache(store, nextTicker)
    },
  })

  const mergedTicker = useMemo(
    () => mergeStickyMarketTicker(cachedTicker, tickerToMarketTicker(streamTicker)),
    [cachedTicker, streamTicker]
  )
  const ticker = useStickyMarketTicker(symbol, mergedTicker)
  const priceChangePercent24h = Number(ticker?.priceChangePercent24h)
  const isPositive = Number.isFinite(priceChangePercent24h) && priceChangePercent24h >= 0
  const fundingSchedule = resolveFundingSchedule(ticker?.nextFundingTime, instrument)
  const fundingSuffix =
    fundingSchedule.kind === 'scheduled' ? (
      <span title={t('funding.nextFunding')}>
        {' / '}
        <FundingCountdown at={fundingSchedule.at} />
      </span>
    ) : fundingSchedule.kind === 'continuous' ? (
      ` / ${t('funding.continuous')}`
    ) : undefined
  const fundingRate = Number(ticker?.fundingRate)
  const fundingRateClassName =
    ticker?.fundingRate && Number.isFinite(fundingRate) && fundingRate !== 0
      ? fundingRate > 0
        ? 'text-chart-mint'
        : 'text-chart-red'
      : undefined

  const items: MarketStatItem[] = [
    {
      label: t('mark.label'),
      value: fmt.text(
        formatPrice(ticker?.markPrice ?? ticker?.lastPrice, pricePrecision, instrument)
      ),
      tooltip: t('mark.tooltip'),
      emphasis: true,
    },
    ...(exchange === Exchange.EXCHANGE_HYPERLIQUID
      ? [
          {
            label: t('oracle.label'),
            value: fmt.text(formatPrice(ticker?.indexPrice, pricePrecision, instrument)),
            tooltip: t('oracle.tooltip'),
          },
        ]
      : []),
    {
      label: t('change24h.label'),
      value: fmt.text(formatPercent(ticker?.priceChangePercent24h)),
      tooltip: t('change24h.tooltip'),
      className: isPositive ? 'text-chart-mint' : 'text-chart-red',
    },
    {
      label: t('lowHigh.label'),
      value: fmt.text(
        `${formatPrice(ticker?.low24h, pricePrecision, instrument)} / ${formatPrice(ticker?.high24h, pricePrecision, instrument)}`
      ),
      tooltip: t('lowHigh.tooltip'),
    },
    {
      label: t('volume.label'),
      value: fmt.text(formatNumber(ticker?.volume24h, 1, 1)),
      tooltip: t('volume.tooltip'),
    },
    {
      label: t('openInterest.label'),
      value: fmt.text(formatNumber(ticker?.openInterest, 0)),
      tooltip: t('openInterest.tooltip'),
    },
    {
      label: t('funding.label'),
      value: fmt.text(formatFunding(ticker?.fundingRate)),
      tooltip: t('funding.tooltip'),
      valueClassName: fundingRateClassName,
      suffix: fundingSuffix,
    },
    {
      label: t('fees.label'),
      value: fmt.text(`${formatFeeRate(makerFee)} / ${formatFeeRate(takerFee)}`),
      tooltip: t('fees.tooltip'),
    },
  ]

  return (
    <TooltipProvider delayDuration={500} disableHoverableContent>
      <div className="flex h-11 shrink-0 items-stretch border-b border-white/10 bg-transparent">
        <ScrollableTabRow contentClassName="gap-6 px-6" fadeClassName="from-background">
          {items.map((item) => (
            <div
              key={item.label}
              className="flex shrink-0 flex-col gap-0.5 text-xs whitespace-nowrap"
            >
              <Tooltip tapToOpen>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="cursor-help border-0 bg-transparent p-0 text-left text-white/45 underline decoration-white/35 decoration-dotted underline-offset-4 transition-colors outline-none hover:text-white/70 focus-visible:text-white/70"
                  >
                    {item.label}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-56 text-xs leading-snug">
                  {item.tooltip}
                </TooltipContent>
              </Tooltip>
              <span
                className={cn(
                  'font-ibm-plex-sans text-sm font-semibold text-white/85 tabular-nums',
                  item.emphasis && 'text-chart-mint',
                  item.className
                )}
              >
                {item.valueClassName ? (
                  <span className={item.valueClassName}>{item.value}</span>
                ) : (
                  item.value
                )}
                {item.suffix}
              </span>
            </div>
          ))}
        </ScrollableTabRow>
      </div>
    </TooltipProvider>
  )
}
